import React, { Dispatch, FC, SetStateAction, useMemo } from "react";

import {
  Table,
  Thead,
  Tbody,
  chakra,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  VStack,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import SortArrowUp from "../../../icons/SortArrowUp";
import SortArrowDown from "../../../icons/SortArrowDown";
import { ButtonPrimary } from "../../UI/Buttons";
import { Link, NavLink } from "react-router-dom";

export interface domainListProps {
  id: number;
  uid: string;
  company_uid: string;
  name: string;
  status: string;
  created_at: Date | string;
  updated_at: Date | string;
}

const DomainsListTable: FC<{ listsData: domainListProps[], verifyOpen:() => void, deleteOpen:() => void, setDomainUid:Dispatch<SetStateAction<string>>, setDomainName:Dispatch<SetStateAction<string>>} > = ({
  listsData, verifyOpen, setDomainUid, setDomainName, deleteOpen
}) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  

  // accessor functions are for when you need to concatenate data e.g firstname and lastname
  const columns = useMemo<ColumnDef<domainListProps>[]>(() => {
    const statusColorsToUse = (status: string) => {
      switch (status) {
        case "verified":
          return {
            color: "#159D6F",
            bg: "rgba(21, 157, 111, 0.04)",
          };
        case "unverified":
          return {
            color: "#7489AA",
            bg: "#7489AA0A",
          };

        default:
      }
    };

    return [
      {
        header: () => <Text>Domain</Text>,
        accessorKey: "domain",
        id: "domain",
        cell: (props: any) => {
          return <Text>{props?.getValue()}</Text>;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        id: "status",
        cell: (props: any) => {
          return (
            <Box
              bg={statusColorsToUse(props?.getValue())?.bg}
              w="fit-content"
              py="4px"
              px="8px"
              rounded="full"
              borderWidth={"0.5px"}
              borderColor={statusColorsToUse(props?.getValue())?.color}
              color={statusColorsToUse(props?.getValue())?.color}
              textTransform="capitalize"
              fontWeight={500}
            >
              {props?.getValue()}
            </Box>
          );
        },
      },
      {
        header: " ",
        accessorKey: "status",
        id: "action",
        cell: (props: any) => {
          return (
            <Menu>
              <MenuButton
                as={Text}
                textDecoration={"underline"}
                cursor={"pointer"}
                width={"fit-content"}
              >
                View Actions
              </MenuButton>
              <MenuList>
                {props?.getValue() !== "verified" ? (
                  <MenuItem onClick={()=>{
                    setDomainUid(props?.row?.original?.uid)
                    verifyOpen()
                  }}>Verify Domain</MenuItem> 
                ) : (
                  <MenuItem><NavLink to={"/account/domain/authentication"}>Authenticate Domain</NavLink></MenuItem>
                )}
                <MenuItem onClick={()=>{
                    setDomainName(props?.row?.original?.domain)
                    setDomainUid(props?.row?.original?.uid)
                    deleteOpen()
                  }}>Delete Domain</MenuItem>
              </MenuList>
            </Menu>
          );
        },
      },
    ];
  }, []);

  const data = listsData;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <>
      <TableContainer
        borderWidth={"1px"}
        borderColor="borders.200"
        rounded={"8px"}
      >
        <Table variant={"simple"}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => {
              return (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <Th
                        key={header.id}
                        textTransform="none"
                        color={"grays.12"}
                        fontSize="xs"
                        letterSpacing={"unset"}
                        fontWeight={500}
                        colSpan={header.colSpan}
                      >
                        <Box display={"flex"}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}

                          {header.column.getCanSort() && (
                            <VStack spacing="2px" my="auto" ml="2">
                              <chakra.button
                                onClick={() => {
                                  header.column.toggleSorting();
                                }}
                              >
                                <SortArrowUp width="8px" />
                              </chakra.button>

                              <chakra.button
                                onClick={() => {
                                  header.column.toggleSorting();
                                }}
                              >
                                <SortArrowDown width="8px" />
                              </chakra.button>
                            </VStack>
                          )}
                        </Box>
                      </Th>
                    );
                  })}
                </Tr>
              );
            })}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    fontSize="xs"
                    color="grays.12"
                    fontWeight={"medium"}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
};

export default DomainsListTable;
